import { Code } from 'lucide-react'
import { useState } from 'react'
import { JsonViewer } from '@/components/json-viewer'
import { Card } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

export interface SearchMatch {
  path: (string | number)[]
  fullPath: string
  value: unknown
  matchedObject: unknown
  rootObject: unknown
}

interface CanvasSearchResultProps {
  result: SearchMatch
}

function truncateValue(value: unknown): string {
  const str = typeof value === 'string' ? value : JSON.stringify(value)
  return str.length > 100 ? `${str.slice(0, 100)}...` : str
}

export function getParentPath(path: (string | number)[]): (string | number)[] {
  let lastChildIndex = -1
  for (let i = path.length - 1; i >= 0; i--) {
    if (typeof path[i] === 'number') {
      lastChildIndex = i
      break
    }
  }

  if (lastChildIndex >= 0) {
    return path.slice(0, lastChildIndex + 1)
  }

  return path.slice(0, -1)
}

function getParentObject(rootObject: unknown, path: (string | number)[]): unknown {
  if (path.length <= 1) {
    return rootObject
  }

  const parentPath = getParentPath(path)
  let current = rootObject

  for (const segment of parentPath) {
    if (current === null || current === undefined) {
      return null
    }
    if (typeof current === 'object' && current !== null) {
      current = (current as Record<string | number, unknown>)[segment]
    } else {
      return null
    }
  }

  return current
}

export function CanvasSearchResult({ result }: CanvasSearchResultProps) {
  const [isOpen, setIsOpen] = useState(false)

  const parentObject = getParentObject(result.rootObject, result.path)

  return (
    <Card className="mb-2 py-0">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full p-3 text-left hover:bg-muted/50">
          <div className="flex items-start gap-2">
            <Code className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs text-primary">{result.fullPath}</div>
              <div className="mt-1 truncate text-xs text-muted-foreground">
                Value: {truncateValue(result.value)}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t p-3">
          <div className="mb-2 text-xs text-muted-foreground">Parent Object:</div>
          <JsonViewer data={parentObject} maxHeight="600px" />
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
